import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Snackbar, Alert, TextField, Checkbox,
  Pagination, Stack, CircularProgress, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { getAllSuppliers, deleteSupplier } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import FormSupplier from './FormSupplier';
import SortableTh from './common/SortableTh';
import { useServerSort } from '../hooks/useServerSort';

function SupplierList() {
  // paginação server-side
  const [pagina, setPagina] = useState(1);   // 1-based
  const [itensPorPagina] = useState(20);

  // dados
  const [fornecedores, setFornecedores] = useState([]);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);

  // busca (enter/botão disparam)
  const [searchText, setSearchText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // UI
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const [idsParaExcluir, setIdsParaExcluir] = useState([]);
  const [mensagemSnackbar, setMensagemSnackbar] = useState('');
  const [snackbarAberto, setSnackbarAberto] = useState(false);
  const [severidadeAlerta, setSeveridadeAlerta] = useState('success');
  const [selecionados, setSelecionados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [form, setForm] = useState({ open: false, id: null });

  // ===== helpers de máscara CPF/CNPJ =====
  const onlyDigits = (v) => String(v || '').replace(/\D/g, '');

  const fmtCPF = (v) => {
    const d = onlyDigits(v).slice(0, 11);
    if (!d) return '';
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2');
  };

  const fmtCNPJ = (v) => {
    const d = onlyDigits(v).slice(0, 14);
    if (!d) return '';
    return d
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  };

  // decide CPF (<=11) ou CNPJ (>11)
  const fmtDoc = (v) => {
    const d = onlyDigits(v);
    if (!d) return '-';
    return d.length <= 11 ? fmtCPF(d) : fmtCNPJ(d);
  };
  // =======================================

  const { orderBy, order, requestSort: aplicarOrdem } = useServerSort('razaoSocial', 'asc');
  const requestSort = (key) => {
    setPagina(1);
    aplicarOrdem(key);
  };

  const carregarPagina = useCallback(async () => {
    try {
      setCarregando(true);
      const resp = await getAllSuppliers({
        indice: pagina,
        qtde: itensPorPagina,
        termo: searchTerm.trim(),
        ordenar: orderBy,
        direcao: order,
      });
      const data = resp.data;
      setFornecedores(data.items || []);
      setTotalPaginas(data.totalPages || 1);
      setTotalItens(data.totalItems || 0);
      setSelecionados([]);
    } catch (e) {
      setMensagemSnackbar('Erro ao carregar fornecedores');
      setSeveridadeAlerta('error');
      setSnackbarAberto(true);
    } finally {
      setCarregando(false);
    }
  }, [pagina, itensPorPagina, searchTerm, orderBy, order]);

  useEffect(() => {
    carregarPagina();
  }, [carregarPagina]);

  // busca por Enter/botão
  const onKeyDownBusca = (e) => {
    if (e.key === 'Enter') {
      setPagina(1);
      setSearchTerm(searchText);
    }
  };
  const acionarBusca = () => {
    setPagina(1);
    setSearchTerm(searchText);
  };

  // seleção
  const fornecedoresPagina = fornecedores;
  const fornecedoresOrdenados = fornecedoresPagina;
  const selecionarTodos = (e) => {
    setSelecionados(e.target.checked ? fornecedoresPagina.map(f => f.id) : []);
  };
  const selecionar = (id) => {
    setSelecionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // exclusão
  const abrirDialogo = (ids) => {
    setIdsParaExcluir(Array.isArray(ids) ? ids : [ids]);
    setDialogoAberto(true);
  };
  const fecharDialogo = () => {
    setDialogoAberto(false);
    setIdsParaExcluir([]);
  };
  const excluir = async () => {
    if (idsParaExcluir.length === 0) return;
    try {
      await Promise.all(idsParaExcluir.map(id => deleteSupplier(id)));
      setMensagemSnackbar(
        idsParaExcluir.length > 1
          ? `${idsParaExcluir.length} fornecedores excluídos com sucesso!`
          : 'Fornecedor excluído com sucesso!'
      );
      setSeveridadeAlerta('success');
      await carregarPagina();
      if (fornecedoresPagina.length === idsParaExcluir.length && pagina > 1) {
        setPagina(pagina - 1);
      }
    } catch (err) {
      setMensagemSnackbar(getApiErrorMessage(err, 'Não foi possível excluir o(s) fornecedor(es).'));
      setSeveridadeAlerta('error');
    } finally {
      setSnackbarAberto(true);
      fecharDialogo();
    }
  };

  const abrirForm = (id = null) => setForm({ open: true, id });
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    setMensagemSnackbar(msg);
    setSeveridadeAlerta('success');
    setSnackbarAberto(true);
    carregarPagina();
  };

  const mudarPagina = (_e, p) => setPagina(p);
  const fecharSnackbar = () => setSnackbarAberto(false);

  // header checkbox
  const todosSelecionados = useMemo(
    () => fornecedoresPagina.length > 0 && selecionados.length === fornecedoresPagina.length,
    [fornecedoresPagina, selecionados]
  );
  const indeterminate = selecionados.length > 0 && !todosSelecionados;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Fornecedores"
        subtitle="Cadastro de fornecedores usado nas entradas de estoque."
        actions={
          <>
            {selecionados.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => abrirDialogo(selecionados)}
              >
                Excluir selecionados ({selecionados.length})
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirForm()}>
              Novo fornecedor
            </Button>
          </>
        }
      />

      {/* Filtros / Busca */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Buscar por razão social, fantasia ou CPF/CNPJ..."
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={onKeyDownBusca}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: searchText && (
              <IconButton size="small" onClick={() => setSearchText('')}>
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }}
          sx={{ flex: 2, minWidth: 280 }}
        />
        <Button variant="outlined" onClick={acionarBusca}>Buscar</Button>
      </Paper>

      {/* Tabela */}
      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox indeterminate={indeterminate} checked={todosSelecionados} onChange={selecionarTodos} />
              </TableCell>
              <SortableTh id="razaoSocial" label="Razão Social" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="nomeFantasia" label="Fantasia" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cnpj" label="CPF/CNPJ" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cidadeUf" label="Cidade/UF" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="telefone" label="Telefone" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="email" label="Email" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carregando ? (
              <TableRow>
                <TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : fornecedoresPagina.length > 0 ? (
              fornecedoresOrdenados.map((f) => (
                <TableRow
                  key={f.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => abrirForm(f.id)}
                >
                  <TableCell padding="checkbox" data-label="Selecionar" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selecionados.includes(f.id)}
                      onChange={() => selecionar(f.id)}
                    />
                  </TableCell>
                  <TableCell data-label="Razão social">{f.razaoSocial || '-'}</TableCell>
                  <TableCell data-label="Fantasia">{f.nomeFantasia || '-'}</TableCell>
                  <TableCell data-label="CPF/CNPJ">{fmtDoc(f.cnpj)}</TableCell>{/* máscara dinâmica */}
                  <TableCell data-label="Cidade/UF">
                    {f.cidade || '-'}{f.estado ? `/${f.estado}` : ''}
                  </TableCell>
                  <TableCell data-label="Telefone">{f.telefone || '-'}</TableCell>
                  <TableCell data-label="Email">{f.email || '-'}</TableCell>
                  <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Editar">
                      <IconButton color="primary" onClick={() => abrirForm(f.id)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton color="error" onClick={() => abrirDialogo(f.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum fornecedor encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {/* Paginação */}
      <Stack spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={mudarPagina} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {fornecedoresPagina.length} de {totalItens} fornecedores (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <EntityFormDialog
        open={form.open}
        title={form.id ? 'Editar fornecedor' : 'Novo fornecedor'}
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={fecharForm}
        maxWidth="lg"
        fullHeight
      >
        <FormSupplier
          key={form.id || 'novo'}
          embedded
          supplierId={form.id}
          onCancel={fecharForm}
          onSaved={aoSalvar}
        />
      </EntityFormDialog>

      {/* Diálogo de confirmação */}
      <Dialog open={dialogoAberto} onClose={fecharDialogo}>
        <DialogTitle>{idsParaExcluir.length > 1 ? 'Excluir Fornecedores' : 'Excluir Fornecedor'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {idsParaExcluir.length > 1
              ? `Tem certeza que deseja excluir os ${idsParaExcluir.length} fornecedores selecionados? Esta ação não pode ser desfeita.`
              : 'Tem certeza que deseja excluir este fornecedor? Esta ação não pode ser desfeita.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogo} color="inherit">Cancelar</Button>
          <Button onClick={excluir} color="error" autoFocus>Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarAberto}
        autoHideDuration={6000}
        onClose={fecharSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={fecharSnackbar} severity={severidadeAlerta} sx={{ width: '100%' }}>
          {mensagemSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SupplierList;
